import React, { useState } from 'react';
import {
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Grid,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Popover,
    List,
    ListItem,
    ListItemText,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    ImageList,
    ImageListItem,
    Modal,
    Checkbox,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { searchInventory } from '../../../Services/Service';
import { checkMultipleInventories } from '../../../Services/Service';
import useMeta from '../../../hooks/useMeta';
import * as XLSX from 'xlsx';
import 'jspdf-autotable';
import moment from "moment";
import InventoryDetailsDialog from '../InventoryDetailsDialog/InventoryDetailsDialog';

const SearchContent = ({ onClose, onAddToProposal, isDialog, defaultCity }) => {
    const [formData, setFormData] = useState({
        city: defaultCity || 'Bengaluru',
        // micromarket: 'Guindy',
        budgetFrom: '6000',
        budgetTo: '30000',
        seatsNeededFrom: '50',
        seatsNeededTo: '60',
        expectedStart: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchResultData, setSearchResultData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedForChecking, setSelectedForChecking] = useState([]);
    const [checkingStatus, setCheckingStatus] = useState({});
    const [filters, setFilters] = useState({
        operator: '',
        locality: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const { cities, getMicromarketsForCity } = useMeta();



    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => {
            if (name === 'city') {
                return {
                    ...prev,
                    [name]: value,
                    micromarket: ''
                };
            }
            return {
                ...prev,
                [name]: value
            };
        });
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            expectedStart: date
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = async () => {
        setLoading(true);
        setError(null);

        const result = await searchInventory(formData);

        if (result.success) {
            setSearchResultData(result.data);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };



    const handleMenuClick = (event, row) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const handleSingleCheck = async (row) => {
        // Set checking status for this inventory
        setCheckingStatus(prev => ({
            ...prev,
            [row.id]: true
        }));

        try {
            // Check availability
            await checkMultipleInventories([row]);

            // Show success message
            setSnackbar({
                open: true,
                message: `Successfully initiated availability check for ${row.inventory}`,
                severity: 'success'
            });

            // Close the action menu
            handleMenuClose();

        } catch (error) {
            console.error('Error checking availability:', error);
            // Show error message
            setSnackbar({
                open: true,
                message: error.message || 'Failed to check inventory availability',
                severity: 'error'
            });
        }

        // Reset checking status
        setCheckingStatus(prev => ({
            ...prev,
            [row.id]: false
        }));
    };

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setDetailsOpen(true);
        setAnchorEl(null); // Close the menu
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedRow(null);
    };

    const handleAddToProposal = (isRecommended) => {
        if (onAddToProposal) {
            const centerWithRecommendedStatus = {
                ...selectedRow,
                isRecommended: isRecommended
            };

            onAddToProposal(centerWithRecommendedStatus);
        }
        handleMenuClose();
        if (onClose) onClose();
    };

    const handleDownloadCSV = () => {
        if (searchResultData.length === 0) return;

        const headers = [
            'Operator',
            'Center Name',
            'Inventory',
            'Billable Seats',
            'Availability Date',
            'Price/Seat',
            'Micromarket',
            'Locality'
        ];

        const data = searchResultData.map(row => [
            row.operatorName || '',
            row.centerName || '',
            row.inventory || '',
            row.billableSeats || '',
            row.availabilityDate || '',
            row.pricePerSeat || '',
            row.microMarket || '',
            row.locality || ''
        ]);

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Search Results');

        XLSX.writeFile(workbook, 'search_results.csv');
    };

    const handleSelectRow = (row) => {
        setSelectedRows(prev => {
            const isSelected = prev.some(item => item.id === row.id);
            if (isSelected) {
                return prev.filter(item => item.id !== row.id);
            } else {
                return [...prev, row];
            }
        });
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            setSelectedRows(currentPageData);
        } else {
            setSelectedRows([]);
        }
    };

    const handleAddSelectedAsRecommended = async () => {
        if (selectedRows.length > 0) {
            for (const row of selectedRows) {
                const centerWithRecommendedStatus = {
                    ...row,
                    isRecommended: true
                };
                await onAddToProposal(centerWithRecommendedStatus);
            }
            setSelectedRows([]);
            onClose();
        }
    };

    const handleCheckboxChange = (event, row) => {
        if (checkingStatus[row.id]) {
            // Don't allow changes while checking
            return;
        }

        if (event.target.checked) {
            setSelectedForChecking(prev => [...prev, row]);
        } else {
            setSelectedForChecking(prev => prev.filter(item => item.id !== row.id));
        }
    };

    const handleSelectAllForChecking = (event) => {
        if (event.target.checked) {
            // Only select rows that aren't currently being checked
            const availableRows = searchResultData.filter(row => !checkingStatus[row.id]);
            setSelectedForChecking(availableRows);
        } else {
            setSelectedForChecking([]);
        }
    };

    const handleCheckAvailability = async () => {
        if (selectedForChecking.length === 0) return;

        // Set checking status for all selected inventories
        const newCheckingStatus = {};
        selectedForChecking.forEach(row => {
            newCheckingStatus[row.id] = true;
        });
        setCheckingStatus(newCheckingStatus);

        try {
            // Check availability for selected inventories
            const result = await checkMultipleInventories(selectedForChecking);

            // Update checking status based on results
            const updatedStatus = { ...newCheckingStatus };
            result.results.forEach(res => {
                if (res.inventoryId) {
                    updatedStatus[res.inventoryId] = false;
                }
            });
            setCheckingStatus(updatedStatus);

            // Show success message
            setSnackbar({
                open: true,
                message: `Successfully initiated availability check for ${selectedForChecking.length} inventories`,
                severity: 'success'
            });

            // Clear selection after checking
            setSelectedForChecking([]);

            // Auto-dismiss dialog if in dialog mode
            if (isDialog && onClose) {
                onClose();
            }

        } catch (error) {
            console.error('Error checking availability:', error);
            // Show error message
            setSnackbar({
                open: true,
                message: error.message || 'Failed to check inventory availability',
                severity: 'error'
            });
            // Reset checking status on error
            setCheckingStatus({});
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const filteredData = searchResultData.filter(row =>
        (!filters.operator || row.operatorName?.toLowerCase().includes(filters.operator.toLowerCase())) &&
        (!filters.locality || row.locality?.toLowerCase().includes(filters.locality.toLowerCase()))
    );
    const currentPageData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );


    const searchForm = (
        <Grid container spacing={2}>
            {/* First Row */}
            <Grid item xs={12} md={6}>
                <FormControl fullWidth required size="small">
                    <InputLabel>City</InputLabel>
                    <Select
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        label="City"
                        IconComponent={KeyboardArrowDownIcon}
                    >
                        {cities.map((city) => (
                            <MenuItem key={city} value={city}>
                                {city}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            {/* Commenting the below field for now */}
            <Grid item xs={12} md={6}></Grid>
            {/* <Grid item xs={12} md={6}>
                 <FormControl fullWidth required size="small">
                     <InputLabel>Micromarket</InputLabel>
                     <Select
                         name="micromarket"
                         value={formData.micromarket}
                         onChange={handleChange}
                         label="Micromarket"
                         disabled={!formData.city}
                         IconComponent={KeyboardArrowDownIcon}
                     >
                         {formData.city && getMicromarketsForCity(formData.city)?.map((market) => (
                             <MenuItem key={market} value={market}>
                                 {market}
                             </MenuItem>
                         ))}
                     </Select>
                 </FormControl>
             </Grid> */}

            {/* Second Row */}
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    required
                    size="small"
                    label="Budget From"
                    name="budgetFrom"
                    value={formData.budgetFrom}
                    onChange={handleChange}
                    type="number"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    required
                    size="small"
                    label="Budget To"
                    name="budgetTo"
                    value={formData.budgetTo}
                    onChange={handleChange}
                    type="number"
                />
            </Grid>

            {/* Third Row */}
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    required
                    size="small"
                    label="Seats From"
                    name="seatsNeededFrom"
                    value={formData.seatsNeededFrom}
                    onChange={handleChange}
                    type="number"
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    required
                    size="small"
                    label="Seats To"
                    name="seatsNeededTo"
                    value={formData.seatsNeededTo}
                    onChange={handleChange}
                    type="number"
                />
            </Grid>
            {/* <Grid item xs={12} md={6}>
                 <LocalizationProvider dateAdapter={AdapterDayjs}>
                     <DatePicker
                         label="Expected Start"
                         value={formData.expectedStart}
                         onChange={handleDateChange}
                         format="DD-MMM-YYYY"
                         slotProps={{
                             textField: {
                                 fullWidth: true,
                                 required: true,
                                 size: "small"
                             }
                         }}
                     />
                 </LocalizationProvider>
             </Grid> */}

            {/* Buttons */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{
                            bgcolor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                            color: 'white',
                            minWidth: 150,
                            height: 40,
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );

    const searchResultsSection = (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Search Results
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {selectedForChecking.length > 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCheckAvailability}
                            startIcon={Object.values(checkingStatus).some(v => v) ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            Check Availability ({selectedForChecking.length})
                        </Button>
                    )}
                    {isDialog && selectedRows.length > 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddSelectedAsRecommended}
                        >
                            Add {selectedRows.length} Selected as Recommended
                        </Button>
                    )}
                    {/* Commenting this for now. To rework this later
                     {searchResultData.length > 0 && (
                         <ButtonGroup variant="outlined" size="small">
                             <Tooltip title="Download as CSV">
                                 <Button
                                     startIcon={<PictureAsPdfIcon />}
                                     onClick={handleDownloadCSV}
                                 >
                                     CSV
                                 </Button>
                             </Tooltip>
                         </ButtonGroup>
                     )} */}
                </Box>
            </Box>
            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Tooltip title="Select to check availability" placement="top">
                                    <Checkbox
                                        indeterminate={selectedForChecking.length > 0 && selectedForChecking.length < searchResultData.length}
                                        checked={searchResultData.length > 0 && selectedForChecking.length === searchResultData.length}
                                        onChange={handleSelectAllForChecking}
                                        color="primary"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: '#1976d2'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Box>
                                    <Typography variant="subtitle2">Operator</Typography>
                                    <TextField
                                        variant="standard"
                                        size="small"
                                        placeholder="Filter operator"
                                        value={filters.operator}
                                        onChange={(e) => setFilters(prev => ({ ...prev, operator: e.target.value }))}
                                    />
                                </Box>
                            </TableCell>
                            <TableCell>Center Name</TableCell>
                            <TableCell>Inventory</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Billable Seats</TableCell>
                            <TableCell>Availability Date</TableCell>
                            <TableCell>Price Per Seat</TableCell>
                            <TableCell>Micromarket</TableCell>
                            <TableCell>
                                <Box>
                                    <Typography variant="subtitle2">Locality</Typography>
                                    <TextField
                                        variant="standard"
                                        size="small"
                                        placeholder="Filter locality"
                                        value={filters.locality}
                                        onChange={(e) => setFilters(prev => ({ ...prev, locality: e.target.value }))}
                                    />
                                </Box>
                            </TableCell>
                            <TableCell>Last Updated</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : searchResultData.length > 0 ? (
                            currentPageData.map((row, index) => (
                                console.log(row),
                                <TableRow
                                    key={index}
                                    selected={isDialog && selectedRows.some(item => item.id === row.id)}
                                    hover={isDialog}
                                    onClick={isDialog ? () => handleSelectRow(row) : undefined}
                                    sx={{ cursor: isDialog ? 'pointer' : 'default' }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedForChecking.some(item => item.id === row.id)}
                                            onChange={(e) => handleCheckboxChange(e, row)}
                                            disabled={checkingStatus[row.id]}
                                            color="primary"
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: '#1976d2'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{row.operatorName}</TableCell>
                                    <TableCell>{row.centerName}</TableCell>
                                    <TableCell>{row.inventory}</TableCell>
                                    <TableCell>{row.inventoryType}</TableCell>
                                    <TableCell>{row.billableSeats}</TableCell>
                                    <TableCell>{row.availabilityDate}</TableCell>
                                    <TableCell>{row.pricePerSeat}</TableCell>
                                    <TableCell>{row.microMarket}</TableCell>
                                    <TableCell>{row.locality}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.lastUpdatedDate ? moment(row.lastUpdatedDate).format('DD-MM-YYYY') : 'N/A'}
                                            size="small"
                                            sx={{
                                                backgroundColor: row.lastUpdatedDate ?
                                                    (moment().diff(moment(row.lastUpdatedDate), 'days') <= 7 ?
                                                        '#e6ffe6' : // light green
                                                        '#fff2e6'   // light orange
                                                    ) : 'inherit',
                                                color: '#111827',
                                                fontWeight: 500
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={(event) => handleMenuClick(event, row)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>

                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={isDialog ? 12 : 11} align="center">
                                    No results found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {searchResultData.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <TablePagination
                            component="div"
                            count={filteredData.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelRowsPerPage="Rows per page:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
                            sx={{
                                '.MuiTablePagination-displayedRows': {
                                    margin: '0 20px'
                                },
                                '.MuiTablePagination-selectLabel': {
                                    marginRight: '10px'
                                }
                            }}
                        />
                    </Box>
                )}
            </TableContainer>
        </Box>
    );

    const content = (
        <>
            {searchForm}
            {searchResultsSection}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <List sx={{ minWidth: 200 }}>
                    <ListItem
                        button
                        onClick={() => handleSingleCheck(selectedRow)}
                        disabled={checkingStatus[selectedRow?.id]}
                    >
                        <ListItemText
                            primary="Check Availability"
                            secondary={checkingStatus[selectedRow?.id] ? "Checking..." : null}
                        />
                        {checkingStatus[selectedRow?.id] && (
                            <CircularProgress size={20} sx={{ ml: 1 }} />
                        )}
                    </ListItem>
                    <ListItem button onClick={() => handleViewDetails(selectedRow)}>
                        <ListItemText primary="View Details" />
                    </ListItem>
                    {isDialog && (
                        <>
                            <ListItem button onClick={() => handleAddToProposal(true)}>
                                <ListItemText primary="Add as Recommended Option" />
                            </ListItem>
                            <ListItem button onClick={() => handleAddToProposal(false)}>
                                <ListItemText primary="Add as Other Option" />
                            </ListItem>
                        </>
                    )}
                </List>
            </Popover>

            {/* Inventory Details Dialog */}
            <InventoryDetailsDialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                center={selectedRow}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        '& .MuiAlert-message': {
                            fontSize: '0.875rem'
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );

    return isDialog ? content : (
        <Paper sx={{ p: 3 }}>
            {content}
        </Paper>
    );
};

const SearchCenterDialog = ({ open = false, onClose, onAddToProposal, isDialog = true, defaultCity }) => {
    // If it's not a dialog, render the content directly
    if (!isDialog) {
        return <SearchContent isDialog={false} defaultCity={defaultCity} />;
    }

    // If it's a dialog, wrap the content in a Dialog component
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    Search Options
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <SearchContent onClose={onClose} onAddToProposal={onAddToProposal} isDialog={true} defaultCity={defaultCity} />
            </DialogContent>
        </Dialog>
    );
};

export default SearchCenterDialog;